import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';
import { getERPExecutions } from '../../../service/api';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const ERPExecutions = () => {
    const navigate = useNavigate();
    const [executions, setExecutions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: ''
    });

    const statusColors = {
        Initiated: '#3b82f6',
        'In Progress': '#f59e0b',
        Completed: '#10b981',
        Aborted: '#dc2626'
    };

    const columnDefs = [
        {
            headerName: 'Execution ID',
            field: 'executionId',
            sortable: true,
            filter: true,
            width: 150,
            cellRenderer: (params: any) => (
                <span
                    style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate(`/external-emergency-preparedness/erp-executions/${params.value}`)}
                >
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Plan Name',
            field: 'planName',
            sortable: true,
            filter: true,
            flex: 1,
            valueGetter: (params: any) => params.data.erpId?.planName || params.data.bcpId?.planName || 'Unknown'
        },
        {
            headerName: 'Status',
            field: 'status',
            sortable: true,
            filter: true,
            width: 140,
            cellRenderer: (params: any) => (
                <span
                    style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: (statusColors as any)[params.value] + '20',
                        color: (statusColors as any)[params.value],
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}
                >
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Progress',
            field: 'progressPercentage',
            sortable: true,
            width: 140,
            cellRenderer: (params: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '60px',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${params.value}%`,
                            height: '100%',
                            backgroundColor: params.value === 100 ? '#10b981' : '#3b82f6',
                            transition: 'width 0.3s'
                        }} />
                    </div>
                    <span style={{ fontSize: '12px' }}>{params.value}%</span>
                </div>
            )
        },
        {
            headerName: 'SLA Status',
            field: 'overallSLA.met',
            sortable: true,
            width: 120,
            cellRenderer: (params: any) => {
                if (params.data.status !== 'Completed') return '-';
                return (
                    <span
                        style={{
                            padding: '4px 8px',
                            borderRadius: '8px',
                            backgroundColor: params.value ? '#10b98120' : '#dc262620',
                            color: params.value ? '#10b981' : '#dc2626',
                            fontSize: '12px',
                            fontWeight: '500'
                        }}
                    >
                        {params.value ? 'Met' : 'Missed'}
                    </span>
                );
            }
        },
        {
            headerName: 'Triggered By',
            field: 'triggeredBy.name',
            sortable: true,
            filter: true,
            width: 150
        },
        {
            headerName: 'Triggered At',
            field: 'triggeredAt',
            sortable: true,
            filter: 'agDateColumnFilter',
            width: 180,
            valueFormatter: (params: any) => new Date(params.value).toLocaleString()
        },
        {
            headerName: 'Deviations',
            field: 'totalDeviations',
            sortable: true,
            width: 120,
            cellRenderer: (params: any) => (
                <span style={{
                    color: params.value > 0 ? '#ea580c' : '#6b7280',
                    fontWeight: params.value > 0 ? 'bold' : 'normal'
                }}>
                    {params.value || 0}
                </span>
            )
        },
        {
            headerName: 'Issues',
            field: 'totalIssues',
            sortable: true,
            width: 100,
            cellRenderer: (params: any) => (
                <span style={{
                    color: params.value > 0 ? '#dc2626' : '#6b7280',
                    fontWeight: params.value > 0 ? 'bold' : 'normal'
                }}>
                    {params.value || 0}
                </span>
            )
        }
    ];

    useEffect(() => {
        fetchExecutions();
    }, [filters]);

    const fetchExecutions = async () => {
        try {
            setLoading(true);
            const response = await getERPExecutions(filters);
            if (response.data.success) {
                setExecutions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching executions:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="px-2 py-4 mt-14 min-h-screen">
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>ERP Executions</h1>
                <button
                    onClick={() => navigate('/external-emergency-preparedness/trigger-erp')}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                        transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                >
                    ⚡ Trigger ERP
                </button>
            </div>

            {/* Filters */}
            <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
            }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Status
                    </label>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db'
                        }}
                    >
                        <option value="">All</option>
                        <option value="Initiated">Initiated</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Aborted">Aborted</option>
                    </select>
                </div>
            </div>

            {/* AG Grid */}
            <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                <AgGridReact
                    rowData={executions}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={20}
                    domLayout="normal"
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default ERPExecutions;
